/**
 * Spracherkennung über die Web-Speech-API.
 *
 * Läuft im Browser, ohne Netzzugriff über uns — Chrome und Safari schicken das
 * Audio an ihre eigenen Dienste. Für die PWA reicht das; wenn wir später
 * bessere Erkennung in hallenden Räumen brauchen, wird hier serverseitige
 * Transkription eingehängt, ohne dass der Aufrufer sich ändert.
 */

export interface SpracheingabeOptionen {
	/** Läuft bei jedem Zwischenergebnis — für die Live-Anzeige beim Sprechen. */
	onZwischenstand?: (text: string) => void;
	/** Läuft, wenn ein Satz fertig erkannt wurde. */
	onSatz: (text: string) => void;
	onFehler?: (grund: string) => void;
	onEnde?: () => void;
}

export interface Spracheingabe {
	start: () => void;
	stop: () => void;
}

type SpeechRecognitionCtor = new () => any;

function ctor(): SpeechRecognitionCtor | null {
	const w = window as unknown as Record<string, unknown>;
	return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as SpeechRecognitionCtor | null;
}

/** Ob das Gerät Spracherkennung überhaupt kann. Firefox kann es bis heute nicht. */
export function spracheVerfuegbar(): boolean {
	return ctor() !== null;
}

export function erstelleSpracheingabe(optionen: SpracheingabeOptionen): Spracheingabe | null {
	const Recognition = ctor();
	if (!Recognition) return null;

	const erkennung = new Recognition();
	erkennung.lang = 'de-DE';
	erkennung.continuous = true;
	erkennung.interimResults = true;

	// Der Nutzer macht beim Diktieren Pausen — Safari beendet die Erkennung dann
	// von sich aus. Solange er nicht selbst gestoppt hat, starten wir neu.
	let laeuft = false;

	erkennung.onresult = (ereignis: any) => {
		for (let i = ereignis.resultIndex; i < ereignis.results.length; i++) {
			const ergebnis = ereignis.results[i];
			const text = String(ergebnis[0].transcript).trim();
			if (!text) continue;
			if (ergebnis.isFinal) {
				optionen.onSatz(text);
			} else {
				optionen.onZwischenstand?.(text);
			}
		}
	};

	erkennung.onerror = (ereignis: any) => {
		// 'no-speech' und 'aborted' sind Alltag, kein Fehler für den Nutzer.
		const grund = String(ereignis.error ?? 'unbekannt');
		if (grund === 'no-speech' || grund === 'aborted') return;
		optionen.onFehler?.(grund);
	};

	erkennung.onend = () => {
		if (laeuft) {
			try {
				erkennung.start();
			} catch {
				laeuft = false;
				optionen.onEnde?.();
			}
		} else {
			optionen.onEnde?.();
		}
	};

	return {
		start() {
			if (laeuft) return;
			laeuft = true;
			try {
				erkennung.start();
			} catch {
				// Doppelter Start wirft — harmlos.
			}
		},
		stop() {
			laeuft = false;
			try {
				erkennung.stop();
			} catch {
				/* egal */
			}
		}
	};
}
